import PropTypes from 'prop-types';

// material-ui
import { Avatar, Box, Container, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { Carousel } from 'antd';

// project import
import LynIcon from 'components/@extended/LynIcon';
import Card from '@mui/material/Card';
import AnimateBox from './AnimateBox';
import cascadeGif from '../../assets/images/gif/cascadeGif.gif';
import documenthub from '../../assets/images/gif/documenthub.svg';
import LeaseManagementSection from './Lease';
// ================================|| SLIDER - ITEMS ||================================ //

const Item = ({ item }) => {
	return (
		<Card
			variant="outlined"
			sx={{
				// p: 1,
				display: 'flex',
				alignItems: 'center',
				// gap: "10px",
				mx: 1,
				my: 0.2,
				// maxWidth: "300px",
				height: '180px',
				borderTopLeftRadius: 5,
				borderTopRightRadius: 15,
				borderBottomLeftRadius: 15,
				borderBottomRightRadius: 5,

				backgroundColor: 'white',
				padding: '25px',
				mb: 3,
				opacity: item.highlight ? 0.75 : 1.0,
				transition: 'background-size 0.2s',
				'&:hover': {
					border: `2px solid white`,
				},
			}}
		>
			<Stack>
				<Avatar variant="circular" style={{ width: '60px', height: '60px', backgroundColor: '#F8FBFF' }}>
					<LynIcon folder="modules" fileName={item?.code} format="svg" sx={{ width: '32px', height: '32px' }} />
				</Avatar>

				<Box>
					<Typography
						sx={{
							cursor: 'pointer',
							fontWeight: 600,
							fontFamily: 'inria sans',
							fontSize: '15px',
							// marginBottom: "10px",
						}}
					>
						{item.text}
					</Typography>
					<Typography
						sx={{
							cursor: 'pointer',
							fontWeight: 400,
							fontFamily: 'inria sans',
							fontSize: '14px',
							maxWidth: '300px',
							textAlign: 'justify',
						}}
					>
						{item?.description}
					</Typography>
				</Box>
			</Stack>
		</Card>
	);
};

Item.propTypes = {
	item: PropTypes.shape({
		text: PropTypes.string,
		highlight: PropTypes.bool,
	}),
};

// ==============================|| LANDING - PARTNER PAGE ||============================== //

export const items = [
	{
		text: 'Accounts Balance Reconciliation',
		code: 'ABR',
		description: 'Safeguards financial statement assertions through standardized reconciliations',
	},
	{
		text: 'Admin & Facilities Management',
		code: 'AFM',
		description: 'Oversees administrative tasks, space allocation, maintenance, and services',
	},
	{
		text: 'Financial Accounting Services',
		code: 'FAS',
		description: 'Manages financial transactions, accounts payable, receivable, and reporting',
	},
	{ text: 'Supply Chain Management', code: 'SCM', description: 'Adminstar end to end procurement, contracts, warehouse and logistics' },
	{
		text: 'Business Planning Management',
		code: 'BPM',
		description: 'Manages strategies with meticulous planning, budgeting and budgetary controls',
	},
	{
		text: 'Fixed Assets Management',
		code: 'FAM',
		description: 'Facilitates asset tracking and transfers for technology and administrative assets',
	},
	{
		text: 'Governance, Process, and Controls',
		code: 'GPC',
		description: 'Establishes governance, controls and ensures compliance with regulations',
	},
	{ text: 'Product & Service Management', code: 'PSM', description: 'Manages product & service lifecycle and performance review' },
	{
		text: 'Sales & Distribution Management',
		code: 'SND',
		description: 'Handles sales orders, delivery and channel partners performance and rewards',
	},
	{ text: 'Lease Management System', code: 'LMS', description: 'Manages lease records, payments, IFRS 16 compliance, and lifecycle' },
	{ text: 'Project Management and Assurance', code: 'PMA', description: 'Delivers projects including end to end governance and assurance' },
	{ text: 'Enterprise Risk Management', code: 'ERM', description: 'Secures organizational interests with proactive risk mitigation' },
	{ text: 'User Access Management', code: 'UAM', description: 'Ensures robust security access control and authorization protocols' },
	{
		text: 'Human Resources Management',
		code: 'HRM',
		description: 'Manages workforces and their lifecycles, payroll, performance and development',
	},
	{
		text: 'Regulatory and Legal Compliance',
		code: 'REL',
		description: 'Facilitates tracking and compliance to regulatory, external and legal requirements',
	},
];

const PartnerBlock = () => {
	const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

	const cardContent = [
		{ label: 'Paperless', code: 'paperless' },
		{ label: 'Ultra User-Friendly', code: 'user_friendly' },
		{ label: 'Super Simplified', code: 'simplified' },
		{ label: 'Total Digitization', code: 'data_digitization' },
		{ label: 'Fully Integrated', code: 'integrated' },
		{ label: '24 / 7 / 365', code: 'hour' },
		{ label: 'Onsite and Remote Access', code: 'onsite_remote' },
		{ label: 'Empowerment for AI & ML', code: 'empowerment' },
	];
	const settings = {
		dots: false,
		infinite: true,
		// arrows: true,
		draggable: true,
		arrowOffset: 12,
		slidesToShow: 4,
		slidesToScroll: 1,
		autoplay: true,
		speed: 2000,
		// autoplaySpeed: 100,
		easing: 'linear',
		effect: 'scrollx',
		rtl: true,
		waitForAnimate: false,

		responsive: [
			{
				breakpoint: 1024,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2,
					infinite: true,
					dots: false,
				},
			},
			{
				breakpoint: 600,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2,
					initialSlide: 2,
				},
			},
			{
				breakpoint: 480,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
				},
			},
		],
	};
	return (
		<Box id="solutions" sx={{ overflow: 'hidden' }}>
			<Box sx={{ bgcolor: '#001E34' }}>
				<Container maxWidth="xl">
					<Grid container alignItems="center" justifyContent="center" sx={{ pt: { md: 15, xs: 12 }, pb: { md: 7, xs: 6 } }}>
						<Grid item xs={12}>
							<Grid container justifyContent="center" sx={{ mb: 4, textAlign: 'center' }}>
								<Grid item xs={10}>
									<Grid container spacing={2} justifyContent="center">
										<Grid item xs={12} sx={{ pt: { xs: 4, sm: 6 } }}>
											<Stack justifyContent="space-between" direction={{ md: 'row' }} gap={{ md: 0, xs: 4 }}>
												<AnimateBox>
													<Typography
														variant="h2"
														sx={{ fontFamily: 'inria sans', fontWeight: '700', color: 'white', letterSpacing: '1.5px' }}
													>
														Our Solutions
													</Typography>
												</AnimateBox>
												<AnimateBox xStart="10%" xEnd="0">
													<Typography
														sx={{
															display: { xs: 'none', sm: 'block' },
															lineHeight: { xs: 1.4, sm: 1.4, md: 1.5 },
															fontFamily: 'inria sans',
															maxWidth: '500px',
															color: 'white',
															textAlign: { xs: 'center', md: 'justify' },
															fontWeight: '500',
															fontSize: '16px',
														}}
													>
														Empower your business with our integrated solutions, thoughtfully designed to enhance
														operational efficiency across all functions, driving growth and success
													</Typography>
												</AnimateBox>
											</Stack>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Carousel {...settings}>
								{items.map((item, index) => (
									<Item key={index} item={item} />
								))}
							</Carousel>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Paperless, user_friendly, simplified */}
			<Box sx={{ bgcolor: '#eaefeb' }}>
				<Grid container direction="column" rowSpacing={5} sx={{ py: { md: 6, xs: 6 } }} justifyContent="center" alignItems="center">
					<Grid item xs={12} justifyContent="center">
						<Typography
							sx={{
								lineHeight: { xs: 1.4, sm: 1.4, md: 1.5 },
								fontFamily: 'inria sans',
								maxWidth: '500px',
								color: 'black',
								textAlign: { xs: 'center', md: 'center' },
								fontWeight: '500',
								fontSize: '16px',
								px: { xs: 2, md: 0 },
							}}
						>
							We invite you to be ahead from others in becoming truly digitized<br></br> and achieving the list of distinctiveness
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Grid container justifyContent="center">
							<Grid item xs={matchDownSM ? 12 : 10}>
								<Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
									{cardContent?.map((item, index) => (
										<Grid item xs={6} md={3} key={index}>
											<AnimateBox xStart="0" xEnd="0" yStart="10%" yEnd="0%">
												<Card
													variant="outlined"
													sx={{
														// p: 1,
														// gap: "10px",
														mx: 1,
														my: 0.2,
														// width: "",
														display: 'flex',
														flexDirection: 'row',
														justifyContent: 'space-around',
														height: '140px',
														borderTopLeftRadius: 5,
														borderTopRightRadius: 15,
														borderBottomLeftRadius: 15,
														borderBottomRightRadius: 5,
														border: (index + 1) % 2 === 1 ? '1px solid #00914F' : '1px solid #214689',
														backgroundColor: 'white',
														padding: '25px',
														mb: 3,
														opacity: item.highlight ? 0.75 : 1.0,
														transition: 'background-size 0.2s',
														'&:hover': {
															// border: `2px solid white`,
														},
													}}
												>
													<Stack direction="column" justifyContent="center" alignItems="center" spacing={2}>
														<Avatar
															variant="circular"
															style={{
																width: '60px',
																height: '60px',
																backgroundColor: 'white',
																border: '1px solid #042f4f',
															}}
														>
															<LynIcon
																folder="landing"
																fileName={item?.code}
																format="svg"
																sx={{ width: '32px', height: '32px' }}
															/>
														</Avatar>

														<Typography
															sx={{
																// cursor: "pointer",
																fontWeight: 400,
																fontFamily: 'inria sans',
																fontSize: '14px',
																maxWidth: '300px',
																textAlign: 'center',
															}}
														>
															{item?.label}
														</Typography>
													</Stack>
												</Card>
											</AnimateBox>
										</Grid>
									))}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>

			{/* <Container maxWidth="xl">
        <Grid container alignItems="center" justifyContent="center" sx={{ pt: { md: 15, xs: 12 }, pb: { md: 7, xs: 6 }, bgcolor: "#042f4f" }}>

          <Grid item xs={12} sx={{ pt: { xs: 4, sm: 6 } }}>
            <Stack justifyContent="space-between" direction={{ md: "row" }} gap={{ md: 0, xs: 4 }}>
              <AnimateBox>
                <Typography variant="h2" sx={{ fontFamily: "inria sans", fontWeight: "700", color: "white", letterSpacing: "1.5px" }}>
                  Cascade Manager
                </Typography>
              </AnimateBox>
              <AnimateBox xStart="10%" xEnd="0">
                <Typography sx={{ lineHeight: { xs: 1.4, sm: 1.4, md: 1.5 }, fontFamily: "inria sans", maxWidth: "500px", color: "white", textAlign: { xs: "center", md: "justify" }, textTransform: "capitalize", fontWeight: "500", fontSize: "16px" }}>
                  Effortlessly cascade tasks with Cascade Manager. Track progress and view real-time updates via a dynamic dashboard. Enjoy seamless communication and an intelligent system that prioritizes and optimizes task management.                  </Typography>
              </AnimateBox>
            </Stack>
          </Grid>
          <Grid item xs={12} sx={{ pt: { xs: 4, sm: 12 } }}>
            <img src={cascadeGif} style={{
              objectFit: "fit",

            }} />
          </Grid>

        </Grid>
      </Container> */}
			{/* <Grid item xs={12}>
        <Slider {...settings} className="slider-container">
          {items.map((item, index) => (
            <Item key={index} item={item} color={theme.palette.common.green} />
          ))}
        </Slider>
      </Grid> */}

			{/* <LeaseManagementSection /> */}
		</Box>
	);
};

export default PartnerBlock;
